"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DocumentList = void 0;
const doshx_controls_web_1 = require("doshx_controls_web");
const react_1 = __importDefault(require("react"));
const react_redux_1 = require("react-redux");
const main_1 = require("../../../../../reducers/slices/main");
const react_router_dom_1 = require("react-router-dom");
const routes_1 = require("../../../../../constants/routes");
const delete_document_thunk_1 = require("../../../../../reducers/slices/document/thunks/delete/delete_document_thunk");
const document_overview_thunk_1 = require("../../../../../reducers/slices/document/thunks/document_overview_thunk");
const document_status_modal_1 = require("./document_status_modal");
const react_fontawesome_1 = require("@fortawesome/react-fontawesome");
const pro_light_svg_icons_1 = require("@fortawesome/pro-light-svg-icons");
const formatDate_1 = require("../../../../../helpers/formatDate");
const DocumentList = ({ status, currentPage, itemsPerPage, onPageChange, onItemsPerPageChange }) => {
    const dispatch = (0, react_redux_1.useDispatch)();
    const navigate = (0, react_router_dom_1.useNavigate)();
    const { documents, documentsCount, documentsLoading } = (0, react_redux_1.useSelector)((state) => state.documentState);
    const { user } = (0, react_redux_1.useSelector)((state) => state.userState);
    // Determine default sort key based on status (always sort by date first, newest first)
    const getDefaultSortKey = () => {
        if (status === "VIEWED")
            return "date_sort_viewed";
        if (status === "COMPLETED")
            return "date_sort_completed";
        return "date_sort"; // For DRAFT, SENT, EXPIRED, or no filter
    };
    const [sort, setSort] = react_1.default.useState({
        key: getDefaultSortKey(),
        direction: "desc", // Newest first
    });
    // Update sort when status changes
    react_1.default.useEffect(() => {
        setSort({
            key: getDefaultSortKey(),
            direction: "desc",
        });
    }, [status]);
    // Calculate pagination based on backend count
    const totalPages = Math.ceil(documentsCount / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    // Handle row click to show status modal
    const handleRowClick = (document) => {
        dispatch((0, main_1.setContentModal)({
            title: document.name,
            content: (react_1.default.createElement(document_status_modal_1.DocumentStatusModal, { document: document, onView: () => {
                    dispatch((0, main_1.setContentModal)(null));
                    if ((document === null || document === void 0 ? void 0 : document.status) === "DRAFT") {
                        navigate(`${routes_1.CONSOLE_ROUTE}${routes_1.DOCUMENT_ROUTE}/${document === null || document === void 0 ? void 0 : document._id}`);
                    }
                    else {
                        navigate(`${routes_1.CONSOLE_ROUTE}${routes_1.DOCUMENT_ROUTE}/${document === null || document === void 0 ? void 0 : document._id}/link`);
                    }
                }, canDelete: document.user_id === (user === null || user === void 0 ? void 0 : user._id), onDelete: () => {
                    dispatch((0, main_1.setContentModal)(null));
                    dispatch((0, main_1.setMessageModal)({
                        title: "Confirm!",
                        messages: [`Are you sure you want to delete document '${document.name}'?`],
                        buttons: [
                            {
                                label: "Cancel",
                                backgroundColor: "bg-white",
                                textColor: "text-primary",
                                borderColor: "border-primary",
                                onClick: () => {
                                    dispatch((0, main_1.setMessageModal)(null));
                                },
                            },
                            {
                                label: "Confirm",
                                onClick: () => {
                                    dispatch((0, delete_document_thunk_1.delete_document_thunk)({
                                        type: "document",
                                        data: { _id: document._id },
                                    })).then((data) => {
                                        if (data.payload) {
                                            dispatch((0, document_overview_thunk_1.document_overview_thunk)());
                                        }
                                    });
                                    dispatch((0, main_1.setMessageModal)(null));
                                },
                            },
                        ],
                    }));
                }, onClose: () => {
                    dispatch((0, main_1.setContentModal)(null));
                } })),
        }));
    };
    const getOwnerName = () => {
        var _a, _b;
        return user ? `${(_a = user.name) !== null && _a !== void 0 ? _a : ""} ${(_b = user.surname) !== null && _b !== void 0 ? _b : ""}`.trim() : "";
    };
    // Handle delete confirmation
    const handleDeleteClick = (e, document) => {
        e.stopPropagation(); // Prevent row click from firing
        dispatch((0, main_1.setMessageModal)({
            title: "Delete Draft",
            messages: [`Are you sure you want to delete '${document.name}'?`],
            buttons: [
                {
                    label: "Cancel",
                    backgroundColor: "bg-white",
                    textColor: "text-primary",
                    borderColor: "border-primary",
                    onClick: () => {
                        dispatch((0, main_1.setMessageModal)(null));
                    },
                },
                {
                    label: "Delete",
                    backgroundColor: "bg-error",
                    textColor: "text-white",
                    onClick: () => {
                        dispatch((0, delete_document_thunk_1.delete_document_thunk)({
                            type: "document",
                            data: { _id: document._id },
                        })).then((data) => {
                            if (data.payload) {
                                dispatch((0, document_overview_thunk_1.document_overview_thunk)());
                            }
                        });
                        dispatch((0, main_1.setMessageModal)(null));
                    },
                },
            ],
        }));
    };
    const ellipsisStyle = {
        display: "inline-block",
        maxWidth: 200,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        verticalAlign: "bottom",
    };
    // Memoize transformed document data for performance
    const transformedDocuments = react_1.default.useMemo(() => {
        return documents.map((x) => {
            const signedCount = Array.isArray(x.signers) ? x.signers.filter((s) => s.signed).length : 0;
            const signersCount = Array.isArray(x.signers) ? x.signers.length : 0;
            // Display signatories as count (number badge) - only_signer takes priority
            const signatories = x.only_signer ? 1 : Array.isArray(x.signers) && x.signers.length > 0 ? signersCount : 0;
            // Build signatories list for hover tooltip
            const signatoriesList = (() => {
                if (x.only_signer)
                    return "Owner only";
                if (Array.isArray(x.signers) && x.signers.length > 0) {
                    return x.signers
                        .map((s) => `${s.first_name || ""} ${s.last_name || ""}`.trim() || s.email)
                        .join("\n");
                }
                return "No signatories";
            })();
            // Build signed list for hover tooltip (SIGNING status)
            const signedList = (() => {
                if (Array.isArray(x.signers) && x.signers.length > 0) {
                    const signed = x.signers.filter((s) => s.signed);
                    if (signed.length === 0)
                        return "No signatures yet";
                    return signed
                        .map((s) => `${s.first_name || ""} ${s.last_name || ""}`.trim() || s.email)
                        .join("\n");
                }
                return "No signatures yet";
            })();
            // Sent date (for SENT status)
            const sentDate = x.sentAt ? (0, formatDate_1.formatDateTime24hr)(x.sentAt) : x.updatedAt ? (0, formatDate_1.formatDateTime24hr)(x.updatedAt) : "";
            const sentDateSort = x.sentAt ? new Date(x.sentAt).getTime() : x.updatedAt ? new Date(x.updatedAt).getTime() : 0;
            // Completed date (fallback to updatedAt if completedAt not available)
            const completedDate = x.completedAt ? (0, formatDate_1.formatDateTime24hr)(x.completedAt) : x.updatedAt ? (0, formatDate_1.formatDateTime24hr)(x.updatedAt) : "";
            const completedDateSort = x.completedAt ? new Date(x.completedAt).getTime() : x.updatedAt ? new Date(x.updatedAt).getTime() : 0;
            // Edited date (createdAt or updatedAt)
            const editedDate = (0, formatDate_1.formatDateTime24hr)(x.updatedAt || x.createdAt);
            const editedDateSort = x.updatedAt ? new Date(x.updatedAt).getTime() : x.createdAt ? new Date(x.createdAt).getTime() : 0;
            return Object.assign(Object.assign({}, x), { signatories,
                signatoriesList,
                signedCount,
                signedList, expiry_reason: x.expiry_reason || "N/A", editedDate,
                editedDateSort,
                sentDate,
                sentDateSort,
                completedDate,
                completedDateSort, owner: getOwnerName() });
        });
    }, [documents, user]);
    // Build columns array based on status
    const getColumns = () => {
        const columns = [];
        // Column 1: Name (always first)
        columns.push({
            displayKey: "name",
            label: "Name",
        });
        // Column 2: Owner (always second)
        columns.push({
            displayKey: "owner",
            label: "Owner",
        });
        // Column 3: Signatories (always third, with hover tooltip)
        columns.push({
            displayKey: "signatories",
            label: "Signatories",
            formatter: (row) => (react_1.default.createElement("span", { className: "inline-flex items-center justify-center h-6 w-6 rounded-full bg-primary text-white text-xs font-semibold cursor-help", title: row.signatoriesList }, row.signatories)),
        });
        // Status-specific column (insert after signatories for SIGNING, or at end for EXPIRED)
        if (status === "SIGNING") {
            // Column 4 for SIGNING: Signed count with hover
            columns.push({
                displayKey: "signedCount",
                label: "Signed",
                formatter: (row) => (react_1.default.createElement("span", { className: "inline-flex items-center justify-center h-6 w-6 rounded-full bg-success text-white text-xs font-semibold cursor-help", title: row.signedList }, row.signedCount)),
            });
        }
        // Column 4/5: Edited Date (always present)
        columns.push({
            displayKey: "editedDateSort",
            label: "Edited Date",
            formatter: (row) => row.editedDate,
        });
        // Status-specific additional columns
        if (status === "SENT") {
            columns.push({
                displayKey: "sentDateSort",
                label: "Date Sent",
                formatter: (row) => row.sentDate,
            });
        }
        if (status === "COMPLETED") {
            columns.push({
                displayKey: "completedDateSort",
                label: "Completed Date",
                formatter: (row) => row.completedDate,
            });
        }
        if (status === "EXPIRED") {
            columns.push({
                displayKey: "expiry_reason",
                label: "Reason",
            });
        }
        // Actions column for DRAFT documents (delete button)
        if (status === "DRAFT") {
            columns.push({
                displayKey: "actions",
                label: "Actions",
                formatter: (row) => (react_1.default.createElement("button", { onClick: (e) => handleDeleteClick(e, row), className: "p-2 text-gray-500 hover:text-error hover:bg-red-50 rounded-full transition-colors", title: "Delete draft" },
                    react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: pro_light_svg_icons_1.faTrash }))),
            });
        }
        return columns;
    };
    return (react_1.default.createElement("div", { className: "flex flex-col gap-4" },
        react_1.default.createElement("div", { className: "flex flex-col gap-8" },
            react_1.default.createElement("div", { className: "max-h-[500px] overflow-x-auto overflow-y-auto", style: { scrollbarWidth: 'thin', scrollbarColor: '#cbd5e0 #f7fafc' } }, documentsLoading ? (
            /* Loading skeleton table */
            react_1.default.createElement("div", { className: "overflow-x-auto bg-white shadow rounded" },
                react_1.default.createElement("table", { className: "min-w-full border-collapse" },
                    react_1.default.createElement("thead", { className: "bg-gray-50" },
                        react_1.default.createElement("tr", null, getColumns().map((col, i) => (react_1.default.createElement("th", { key: i },
                            react_1.default.createElement(doshx_controls_web_1.LabelControl, { label: col.label, size: "small", className: "px-4 py-2 text-left cursor-pointer", textColor: "text-gray-700" })))))),
                    react_1.default.createElement("tbody", null, Array.from({ length: itemsPerPage }).map((_, rowIndex) => (react_1.default.createElement("tr", { key: rowIndex, className: "animate-pulse" }, getColumns().map((_, colIndex) => (react_1.default.createElement("td", { key: colIndex, className: "px-4 py-2 border-t bg-gray-200" })))))))))) : (
            /* Data table - CRM Style */
            react_1.default.createElement(doshx_controls_web_1.TableControl, { columns: getColumns(), data: transformedDocuments, sort: {
                    value: sort,
                    onChange: (newSort) => setSort(newSort),
                }, onRowClicked: handleRowClick }))),
            documentsCount > 0 && !documentsLoading && (react_1.default.createElement("div", { className: "flex flex-wrap justify-between items-center bg-white p-4 rounded-md text-sm" },
                react_1.default.createElement("div", { className: "flex items-center gap-3" },
                    react_1.default.createElement("button", { onClick: () => {
                            if (currentPage > 1) {
                                onPageChange(currentPage - 1);
                            }
                        }, disabled: currentPage === 1, className: `flex items-center justify-center w-8 h-8 rounded-full border-none transition-colors ${currentPage === 1 ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-gray-200 text-gray-700 hover:bg-gray-300 cursor-pointer"}` },
                        react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: pro_light_svg_icons_1.faChevronLeft, className: "text-sm" })),
                    react_1.default.createElement(doshx_controls_web_1.LabelControl, { label: `Page ${currentPage}`, size: "small", bold: true, textColor: "text-black" }),
                    react_1.default.createElement("button", { onClick: () => {
                            if (currentPage < totalPages) {
                                onPageChange(currentPage + 1);
                            }
                        }, disabled: currentPage === totalPages, className: `flex items-center justify-center w-8 h-8 rounded-full border-none transition-colors ${currentPage === totalPages ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-gray-200 text-gray-700 hover:bg-gray-300 cursor-pointer"}` },
                        react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: pro_light_svg_icons_1.faChevronRight, className: "text-sm" }))),
                react_1.default.createElement("div", { className: "hidden md:block text-gray-600" },
                    "Showing ",
                    startIndex + 1,
                    "-",
                    Math.min(endIndex, documentsCount),
                    " of ",
                    documentsCount),
                react_1.default.createElement("div", { className: "flex items-center gap-2" },
                    react_1.default.createElement(doshx_controls_web_1.LabelControl, { label: "Page Size", size: "small", bold: true, textColor: "text-black" }),
                    react_1.default.createElement(doshx_controls_web_1.DropdownControl, { size: "small", options: [
                            { label: "10", value: "10" },
                            { label: "25", value: "25" },
                            { label: "50", value: "50" },
                            { label: "100", value: "100" },
                        ], value: itemsPerPage.toString(), onChange: (value) => {
                            onItemsPerPageChange(parseInt(value));
                        }, borderColor: "border-gray-300", textColor: "text-black" })))))));
};
exports.DocumentList = DocumentList;
